import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Controller, FormProvider, useForm, type Path } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { formatMoney } from '@paytrie/common/utils'

import { Button } from '@/components/Button/Button'
import { SimpleWalletConnect } from '@/components/Button/AddWalletConnect'
import { WidgetLayout } from '@/pages/Buy/WidgetLayout'
import { LoadingIcon } from '@/components/LoadingIcon'
import { OtcContactSupport } from '@/components/OtcContactSupport'
import { TextField, FieldError } from '@/components/FormField'
import {
  BackButton,
  DropdownMenuSelect,
  ErrorBlockText,
  TextInput,
} from '@/components/widget-components'
import { isValidWalletAddressForNetwork } from '@/pages/Buy/utils'
import { replaceLegacyPaytrieWallet } from '@/utils/legacyPaytrieWallet'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { TransactionType, useBuySellStore } from '@/reducers/transactionReducer'
import { createOtcRequest } from '@/requests/otc'
import { otcKeys } from '@/hooks/useOtc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useBankAccounts } from '@/hooks/useBankAccounts'
import {
  trackOtcBankAccountSelected,
  trackOtcRequestStarted,
  trackOtcRequestSubmitFailed,
  trackOtcRequestSubmitted,
  trackOtcSavedAccountsLoadFailed,
  trackOtcStepFailed,
  trackOtcStepSubmitted,
  trackOtcStepViewed,
} from '@/utils/analytics'
import {
  buildOtcSchema,
  formatBankAccountLabel,
  OTC_FORM_DEFAULTS,
  PURPOSE_OPTIONS,
  resolveOtcContext,
  SOURCE_OF_FUNDS_OPTIONS,
  type OtcFormValues,
  type OtcFormOutput,
} from './OtcSubmit.logic'

type Option = { value: string; labelKey: string }

function SelectField({
  name,
  label,
  options,
  testId,
}: {
  name: Path<OtcFormValues>
  label: string
  options: readonly Option[]
  testId: string
}) {
  const { t } = useTranslation()

  return (
    <Controller<OtcFormValues>
      name={name}
      render={({ field, fieldState }) => (
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium" htmlFor={testId}>
            {label}
          </label>
          <div className="relative">
            <select
              id={testId}
              data-testid={testId}
              className="w-full appearance-none border border-gray-300 dark:border-neutral-700 rounded py-3 pl-3 pr-10 text-base bg-white dark:bg-pebble dark:text-white"
              value={String(field.value ?? '')}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
            >
              <option value="">{t('otc.form.select')}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
              aria-hidden="true"
            />
          </div>
          <FieldError
            message={fieldState.error?.message}
            id={`${testId}-error`}
            reserveSpace={false}
          />
        </div>
      )}
    />
  )
}

export function OtcSubmit() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { enableOtc, isLoading: flagsLoading } = useFeatureFlags()
  const [instructionsOpen, setInstructionsOpen] = useState(true)
  const [step, setStep] = useState<1 | 2>(1)

  const [searchParams] = useSearchParams()
  const transactionType = useBuySellStore.use.transactionType()
  const fiatAmount = useBuySellStore.use.fiatAmount()
  const stableCoinAmount = useBuySellStore.use.stableCoinAmount()
  const storeNetwork = useBuySellStore.use.network()
  const storeStableCoin = useBuySellStore.use.stableCoin()

  const storeIsSell = transactionType === TransactionType.SELL
  const storeCoinLabel = `${storeStableCoin}-${storeNetwork}`
  const { leftSideLabel, rightSideLabel, leftSideValue, isSell, network } =
    resolveOtcContext(searchParams, {
      leftSideLabel: storeIsSell ? storeCoinLabel : 'CAD',
      rightSideLabel: storeIsSell ? 'CAD' : storeCoinLabel,
      leftSideValue: storeIsSell ? stableCoinAmount : fiatAmount,
      isSell: storeIsSell,
      network: storeNetwork,
    })

  const side = isSell ? 'sell' : 'buy'
  const isFormAvailable = !flagsLoading && enableOtc && leftSideValue > 0

  const schema = useMemo(
    () => buildOtcSchema({ isSell, network, t }),
    [isSell, network, t]
  )
  const methods = useForm<OtcFormValues, unknown, OtcFormOutput>({
    resolver: zodResolver(schema),
    defaultValues: { ...OTC_FORM_DEFAULTS, leftSideValue },
    mode: 'onTouched',
  })
  const {
    handleSubmit,
    setError,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = methods
  const handleWalletConnectError = useCallback(
    (error: Error) => {
      setError('wallet', { message: error.message, type: 'manual' })
    },
    [setError]
  )

  const currentUserQuery = useCurrentUser(isSell)
  const userId = isSell ? currentUserQuery.data?.id : undefined
  const bankAccountsQuery = useBankAccounts(userId)
  const bankAccounts = bankAccountsQuery.data ?? []
  const loadingSavedAccounts =
    isSell &&
    (currentUserQuery.isLoading ||
      (!!userId && !bankAccountsQuery.isSuccess && !bankAccountsQuery.isError))
  const savedAccountsErrored =
    isSell && (currentUserQuery.isError || bankAccountsQuery.isError)

  const bankMode = watch('bankMode')
  const bankDetailsId = watch('bankDetailsId')

  const didTrackStart = useRef(false)
  useEffect(() => {
    if (!isFormAvailable || didTrackStart.current) return
    didTrackStart.current = true
    trackOtcRequestStarted({
      side,
      leftSideLabel,
      rightSideLabel,
      leftSideValue,
    })
  }, [isFormAvailable, side, leftSideLabel, rightSideLabel, leftSideValue])

  useEffect(() => {
    if (!isFormAvailable) return
    trackOtcStepViewed(step === 1 ? 'details' : 'bank_details', side)
  }, [isFormAvailable, step, side])

  useEffect(() => {
    if (!savedAccountsErrored) return
    trackOtcSavedAccountsLoadFailed()
  }, [savedAccountsErrored])

  const didInitBankSelection = useRef(false)
  useEffect(() => {
    if (!isSell || didInitBankSelection.current || bankAccounts.length === 0) {
      return
    }
    didInitBankSelection.current = true
    setValue('bankMode', 'saved')
    setValue('bankDetailsId', bankAccounts[0].id)
  }, [isSell, bankAccounts, setValue])

  const handleBankSelection = useCallback(
    (value: string) => {
      if (value === 'new') {
        trackOtcBankAccountSelected('new')
        setValue('bankMode', 'new')
        setValue('bankDetailsId', null)
        return
      }
      trackOtcBankAccountSelected('saved')
      setValue('bankMode', 'saved')
      setValue('bankDetailsId', Number(value), { shouldValidate: true })
    },
    [setValue]
  )

  const handleNext = async () => {
    trackOtcStepSubmitted('details', side)
    const isStepValid = await trigger(['sourceOfFunds', 'purpose', 'wallet'])
    if (!isStepValid) {
      trackOtcStepFailed({
        step: 'details',
        side,
        reason: 'validation_failed',
      })
      return
    }
    setStep(2)
  }

  const finalStep = isSell ? 'bank_details' : 'details'

  const onInvalid = () => {
    trackOtcStepSubmitted(finalStep, side)
    trackOtcStepFailed({
      step: finalStep,
      side,
      reason: 'validation_failed',
    })
  }

  const onValid = async (values: OtcFormOutput) => {
    trackOtcStepSubmitted(finalStep, side)
    const bankPayload =
      isSell && values.bankMode === 'saved'
        ? { bankDetailsId: values.bankDetailsId ?? undefined }
        : isSell
          ? {
              bankDetails: {
                accountHolderName: values.accountHolderName,
                institutionNumber: values.institutionNumber,
                transitNumber: values.transitNumber,
                accountNumber: values.accountNumber,
                nickname: values.nickname.trim() || undefined,
              },
            }
          : {}

    try {
      const created = await createOtcRequest({
        leftSideLabel,
        leftSideValue: values.leftSideValue,
        rightSideLabel,
        wallet: replaceLegacyPaytrieWallet(values.wallet),
        purpose: values.purpose,
        sourceOfFunds: values.sourceOfFunds,
        ...bankPayload,
      })
      trackOtcRequestSubmitted({
        otcRequestId: created.id,
        side,
        bankMode: isSell ? values.bankMode : null,
      })
      await queryClient.invalidateQueries({ queryKey: otcKeys.list() })
      navigate(`/otc-requests/${created.id}`)
    } catch (error) {
      trackOtcRequestSubmitFailed(
        side,
        error instanceof Error ? error.message : 'unknown_error'
      )
      setError('root', {
        message: t('otc.submit.submitError'),
      })
    }
  }

  if (flagsLoading) {
    return (
      <div className="flex justify-center p-10">
        <LoadingIcon />
      </div>
    )
  }

  if (!enableOtc || leftSideValue <= 0) {
    return <Navigate to="/" replace />
  }

  return (
    <WidgetLayout>
      <div className="flex items-center justify-between">
        <BackButton
          onClick={() =>
            step === 2
              ? setStep(1)
              : navigate('/', { state: { fromOtc: true } })
          }
          aria-label={t('widget.navigation.back')}
          data-testid="btn-otc-submit-back"
        >
          {t('widget.navigation.back')}
        </BackButton>
        <DropdownMenuSelect />
      </div>

      <h1 className="mt-16 text-lg font-semibold uppercase md:mt-12">
        {t('otc.submit.title')}
      </h1>

      <div className="rounded border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-pebble text-sm">
        <button
          type="button"
          className="flex w-full items-center justify-between p-4 font-medium text-gray-900 dark:text-white"
          onClick={() => setInstructionsOpen((open) => !open)}
          aria-expanded={instructionsOpen}
          aria-controls="otc-instructions"
          data-testid="btn-otc-instructions-toggle"
        >
          <span>{t('otc.submit.instructionsTitle')}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
              instructionsOpen ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </button>
        {instructionsOpen && (
          <ol
            id="otc-instructions"
            className="list-decimal space-y-1 pb-4 pl-9 pr-4 text-gray-600 dark:text-gray-300"
            data-testid="list-otc-instructions"
          >
            <li>{t('otc.submit.instruction1')}</li>
            {isSell ? (
              <li>{t('otc.submit.sellFollowUp')}</li>
            ) : (
              <>
                <li>{t('otc.submit.instruction2')}</li>
                <li>{t('otc.submit.instruction3')}</li>
              </>
            )}
          </ol>
        )}
      </div>

      <div className="rounded border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-pebble p-4 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">
            {t('otc.summary.youSend')}
          </span>
          <span className="font-medium dark:text-white">
            {formatMoney(leftSideValue)} {leftSideLabel}
          </span>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-gray-500 dark:text-gray-400">
            {t('otc.summary.youReceive')}
          </span>
          <span className="font-medium dark:text-white">{rightSideLabel}</span>
        </div>
      </div>

      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onValid, onInvalid)}
          data-testid="container-otc-submit"
        >
          {step === 1 && (
            <>
              <SelectField
                name="sourceOfFunds"
                label={t('otc.form.sourceOfFunds')}
                options={SOURCE_OF_FUNDS_OPTIONS}
                testId="select-otc-source-of-funds"
              />
              <SelectField
                name="purpose"
                label={t('otc.form.intendedUse')}
                options={PURPOSE_OPTIONS}
                testId="select-otc-purpose"
              />

              <Controller<OtcFormValues>
                name="wallet"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <label
                        className="text-sm font-medium"
                        htmlFor="input-otc-wallet"
                      >
                        {isSell
                          ? t('otc.form.walletSend')
                          : t('otc.form.walletReceive')}
                      </label>
                      <SimpleWalletConnect
                        network={network}
                        onWalletConnect={(address) =>
                          field.onChange(replaceLegacyPaytrieWallet(address))
                        }
                        onError={handleWalletConnectError}
                        isValidManualAddress={isValidWalletAddressForNetwork(
                          String(field.value ?? ''),
                          network
                        )}
                        currentWallet={String(field.value ?? '')}
                      />
                    </div>
                    <TextInput
                      data-testid="input-otc-wallet"
                      className="!block overflow-hidden text-ellipsis whitespace-nowrap"
                      placeholder={t('addWallet.placeholder')}
                      value={String(field.value ?? '')}
                      maxLength={50}
                      onChange={(e) =>
                        field.onChange(
                          replaceLegacyPaytrieWallet(e.target.value)
                        )
                      }
                      onBlur={field.onBlur}
                    />
                    <FieldError
                      message={fieldState.error?.message}
                      id="input-otc-wallet-error"
                      reserveSpace={false}
                    />
                  </div>
                )}
              />
            </>
          )}

          {isSell && step === 2 && (
            <div className="flex flex-col gap-4 rounded border border-gray-200 p-4">
              <div className="text-sm font-medium">
                {t('otc.form.bankSectionTitle')}
              </div>

              {loadingSavedAccounts ? (
                <div className="flex justify-center py-4">
                  <LoadingIcon />
                </div>
              ) : (
                <>
                  {savedAccountsErrored && (
                    <div
                      className="text-sm text-amber-600"
                      data-testid="text-otc-bank-accounts-error"
                    >
                      {t('otc.form.savedAccountsError')}
                    </div>
                  )}
                  {bankAccounts.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <label
                        className="text-sm font-medium"
                        htmlFor="select-otc-bank-account"
                      >
                        {t('otc.form.savedAccount')}
                      </label>
                      <div className="relative">
                        <select
                          id="select-otc-bank-account"
                          data-testid="select-otc-bank-account"
                          className="w-full appearance-none border border-gray-300 dark:border-neutral-700 rounded py-3 pl-3 pr-10 text-base bg-white dark:bg-pebble dark:text-white"
                          value={
                            bankMode === 'new'
                              ? 'new'
                              : String(bankDetailsId ?? '')
                          }
                          onChange={(e) => handleBankSelection(e.target.value)}
                        >
                          {bankAccounts.map((account) => (
                            <option key={account.id} value={String(account.id)}>
                              {formatBankAccountLabel(account)}
                            </option>
                          ))}
                          <option value="new">
                            {t('otc.form.addNewAccount')}
                          </option>
                        </select>
                        <ChevronDown
                          className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                          aria-hidden="true"
                        />
                      </div>
                      <FieldError
                        message={errors.bankDetailsId?.message}
                        id="select-otc-bank-account-error"
                        reserveSpace={false}
                      />
                    </div>
                  )}

                  {bankMode === 'new' && (
                    <>
                      <TextField<OtcFormValues>
                        name="accountHolderName"
                        placeholder={t('otc.form.accountHolder')}
                        testId="input-otc-account-holder"
                      />
                      <TextField<OtcFormValues>
                        name="institutionNumber"
                        placeholder={t('otc.form.institutionNumber')}
                        testId="input-otc-institution"
                      />
                      <TextField<OtcFormValues>
                        name="transitNumber"
                        placeholder={t('otc.form.transitNumber')}
                        testId="input-otc-transit"
                      />
                      <TextField<OtcFormValues>
                        name="accountNumber"
                        placeholder={t('otc.form.accountNumber')}
                        testId="input-otc-account-number"
                      />
                      <TextField<OtcFormValues>
                        name="nickname"
                        placeholder={t('otc.form.nickname')}
                        testId="input-otc-nickname"
                      />
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <ErrorBlockText data-testid="error-otc-submit">
            {errors.root?.message ?? ''}
          </ErrorBlockText>

          {isSell && step === 1 ? (
            <Button
              type="button"
              className="inline-flex items-center py-4 px-4 w-full justify-center"
              onClick={handleNext}
              data-testid="btn-otc-next"
            >
              {t('otc.submit.next')}
            </Button>
          ) : isSell ? (
            <div className="flex gap-3">
              <Button
                type="button"
                color="outline"
                className="inline-flex flex-1 items-center py-4 px-4 justify-center"
                onClick={() => setStep(1)}
                data-testid="btn-otc-back"
              >
                {t('otc.submit.back')}
              </Button>
              <Button
                type="submit"
                className="inline-flex flex-1 items-center py-4 px-4 justify-center"
                disabled={isSubmitting || loadingSavedAccounts}
                data-testid="btn-otc-submit"
              >
                {isSubmitting
                  ? t('otc.submit.submitting')
                  : t('otc.submit.submitButton')}
              </Button>
            </div>
          ) : (
            <Button
              type="submit"
              className="inline-flex items-center py-4 px-4 w-full justify-center"
              disabled={isSubmitting}
              data-testid="btn-otc-submit"
            >
              {isSubmitting
                ? t('otc.submit.submitting')
                : t('otc.submit.submitButton')}
            </Button>
          )}
        </form>
      </FormProvider>

      <div className="mt-2 mb-6">
        <OtcContactSupport screen="submit" />
      </div>
    </WidgetLayout>
  )
}

export default React.memo(OtcSubmit)
